import { motion, useReducedMotion } from 'framer-motion';

// Wspólny „język" animacji dla całego serwisu.
const EASE = [0.22, 1, 0.36, 1]; // miękkie wyhamowanie (ease-out-expo)

/**
 * Pojedynczy element wjeżdżający przy przewinięciu (fade + przesunięcie).
 * <Reveal as="p" delay={0.1}>tekst</Reveal>
 */
export const Reveal = ({
  children,
  as = 'div',
  className = '',
  delay = 0,
  y = 26,
  x = 0,
  duration = 0.7,
  amount = 0.2,
  once = true,
  ...rest
}) => {
  const reduce = useReducedMotion();
  const Tag = motion[as] || motion.div;

  if (reduce) {
    const Plain = as;
    return <Plain className={className} {...rest}>{children}</Plain>;
  }

  return (
    <Tag
      className={className}
      initial={{ opacity: 0, y, x }}
      whileInView={{ opacity: 1, y: 0, x: 0 }}
      viewport={{ once, amount }}
      transition={{ duration, delay, ease: EASE }}
      {...rest}
    >
      {children}
    </Tag>
  );
};

/**
 * Kontener, którego dzieci pojawiają się kaskadowo.
 * Dzieci owiń w <RevealItem>.
 */
export const RevealGroup = ({ children, className = '', stagger = 0.09, delay = 0, amount = 0.15, as = 'div', ...rest }) => {
  const reduce = useReducedMotion();
  const Tag = motion[as] || motion.div;

  if (reduce) {
    const Plain = as;
    return <Plain className={className} {...rest}>{children}</Plain>;
  }

  return (
    <Tag
      className={className}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount }}
      variants={{ hidden: {}, visible: { transition: { staggerChildren: stagger, delayChildren: delay } } }}
      {...rest}
    >
      {children}
    </Tag>
  );
};

export const RevealItem = ({ children, className = '', y = 22, as = 'div', ...rest }) => {
  const reduce = useReducedMotion();
  const Tag = motion[as] || motion.div;

  if (reduce) {
    const Plain = as;
    return <Plain className={className} {...rest}>{children}</Plain>;
  }

  return (
    <Tag
      className={className}
      variants={{
        hidden: { opacity: 0, y },
        visible: { opacity: 1, y: 0, transition: { duration: 0.65, ease: EASE } },
      }}
      {...rest}
    >
      {children}
    </Tag>
  );
};

/**
 * Tytuł, w którym słowa wyjeżdżają kolejno zza „maski" — charakterystyczny
 * efekt nowoczesnych stron. Zachowuje poprawne łamanie wierszy.
 */
export const RevealHeading = ({ text, as = 'h2', className = '', delay = 0, stagger = 0.07 }) => {
  const reduce = useReducedMotion();
  const Tag = motion[as] || motion.h2;
  const content = typeof text === 'string' ? text : String(text ?? '');

  if (reduce) {
    const Plain = as;
    return <Plain className={className}>{content}</Plain>;
  }

  const words = content.split(' ').filter(Boolean);

  return (
    <Tag
      className={className}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.4 }}
      variants={{ hidden: {}, visible: { transition: { staggerChildren: stagger, delayChildren: delay } } }}
      aria-label={content}
    >
      {words.map((word, i) => (
        // maska: pb/-mb daje miejsce na „ogonki" (ą, ę, g, y), żeby nic nie było przycięte
        <span key={`${word}-${i}`} className="inline-block overflow-hidden pb-[0.14em] -mb-[0.14em] align-bottom" aria-hidden>
          <motion.span
            className="inline-block"
            variants={{
              hidden: { y: '110%', opacity: 0 },
              visible: { y: 0, opacity: 1, transition: { duration: 0.7, ease: EASE } },
            }}
          >
            {word}
            {i < words.length - 1 ? ' ' : ''}
          </motion.span>
        </span>
      ))}
    </Tag>
  );
};

export default Reveal;
